import { useMemo } from 'react';
import { brandConfig, isTecalor } from '@common/brand/brandConfig';
import ApplicationTitle from '@common/components/PageHeader/ApplicationTitle';
import {
  serviceWelt,
  serviceWeltDemo,
  userManagement,
} from '@common/components/PageHeader/constants';
import HeaderLinks from '@common/components/PageHeader/HeaderLinks';

interface ApplicationSwitcherProps {
  hasOppositeApp?: boolean;
  isDemoMode: boolean;
  canAccessUserManagement?: boolean;
  isDeviceRegistration?: boolean;
}

function ApplicationSwitcher({
  hasOppositeApp,
  isDemoMode,
  canAccessUserManagement = false,
  isDeviceRegistration,
}: Readonly<ApplicationSwitcherProps>) {
  const appName = useMemo(() => {
    if (APP_TYPE === 'um') {
      return userManagement;
    }
    if (isTecalor) {
      return isDemoMode ? brandConfig.demoName : brandConfig.headerTitle;
    }
    return isDemoMode ? serviceWeltDemo : serviceWelt;
  }, [isDemoMode]);

  // Device registration is a standalone, unauthenticated page: no app name (and no home link
  // hiding inside it), only the ISG / Device registration label rendered by HeaderLinks.
  if (isDeviceRegistration) {
    return (
      <HeaderLinks
        serviceWeltLabel={isTecalor ? brandConfig.headerTitle : serviceWelt}
        showsAppSwitch={false}
      />
    );
  }

  // No opposite app, or demo mode → just the plain title (no pills, no Toolbox).
  if (!hasOppositeApp || isDemoMode) {
    return <ApplicationTitle title={appName} />;
  }

  // The app switch (ServiceWelt / User management pills) only appears when the user can
  // reach the other app. Otherwise the current app stays a plain title, with the Toolbox.
  const showsAppSwitch = APP_TYPE === 'um' || canAccessUserManagement;

  // Title first so the Toolbox pill stays rightmost. When the switch pills are shown they
  // replace the title on desktop (mobileOnly); otherwise the plain title shows everywhere.
  return (
    <>
      <ApplicationTitle title={appName} mobileOnly={showsAppSwitch} />
      <HeaderLinks
        serviceWeltLabel={isTecalor ? brandConfig.headerTitle : serviceWelt}
        showsAppSwitch={showsAppSwitch}
      />
    </>
  );
}

export default ApplicationSwitcher;
